import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Footer } from "@/components/Footer";
import { Navbar } from "@/components/Navbar";
import { SEOBreadcrumbs } from "@/components/SEOBreadcrumbs";
import { useAnalyticsEvents } from "@/hooks/use-analytics";
import { BOOK_CALL_URL } from "@/lib/contact";
import { glossary, offerings, openSource, steps } from "@/data/offerings";

const clients = [
  { name: "Callbook.ai", url: "https://www.callbook.ai/" },
  { name: "RentAHuman", url: "https://rentahuman.ai/" },
  { name: "Tenkara", url: "https://app.tenkara.ai/" },
  { name: "SecureNetMe", url: "https://securenetme.com/" },
  { name: "Naytive", url: "https://naytive.com/" },
  { name: "Arqio", url: "https://www.arqio.ai/" },
];

// One request, start to finish, the way it looks once the harness is in place.
const exampleRun = [
  { tag: "skill", text: "follow-up-playbook loaded" },
  { tag: "mcp", text: "crm.search_deals  last_reply > 21 days" },
  { tag: "mcp", text: "gmail.create_draft  × 14" },
];

const Index = () => {
  const { trackButtonClick } = useAnalyticsEvents();
  const location = useLocation();

  // Navbar links like /#process land here; scroll to the section once rendered.
  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth" }));
  }, [location.hash, location.key]);

  const bookCall = (where: string) => () => trackButtonClick("Book a call", where);

  return (
    <>
      <SEOBreadcrumbs title="Home" />
      <div className="flex min-h-screen flex-col bg-black text-white">
        <Navbar />

        <main className="flex-1">
          {/* Hero */}
          <section className="relative overflow-hidden border-b border-white/10 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:64px_64px]">
            <div className="container grid items-center gap-14 pb-20 pt-32 md:pt-40 lg:min-h-[min(88vh,860px)] lg:grid-cols-[1.25fr_1fr]">
              <div>
                <p className="mb-6 inline-flex w-fit items-center gap-2 border-2 border-white/20 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-white/70">
                  <span className="h-2 w-2 bg-vision-gold" aria-hidden="true" />
                  AI transformation &amp; harness company
                </p>
                <h1 className="text-5xl font-black uppercase leading-[0.95] tracking-tight sm:text-6xl xl:text-7xl">
                  We build the <span className="text-vision-gold">harness</span> that puts AI to work
                </h1>
                <p className="mt-8 max-w-xl text-lg leading-relaxed text-white/70 md:text-xl">
                  An AI model on its own can only chat. Virelity sets up what goes around it: custom MCP servers that
                  connect it to your tools, skills that teach it your process, and agents that finish the job. Then
                  we train your team to run it.
                </p>
                <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                  <a
                    href={BOOK_CALL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={bookCall("Hero")}
                    className="inline-flex items-center justify-center gap-2 border-2 border-black bg-vision-gold px-8 py-4 text-base font-black uppercase tracking-wider text-black shadow-[6px_6px_0_0_#fff] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[8px_8px_0_0_#fff]"
                  >
                    Book a free call <ArrowRight className="h-5 w-5" />
                  </a>
                  <Link
                    to="/services"
                    className="inline-flex items-center justify-center gap-2 border-2 border-white/30 px-8 py-4 text-base font-bold uppercase tracking-wider text-white transition-colors hover:border-white"
                  >
                    What we set up
                  </Link>
                </div>
              </div>

              {/* Example run */}
              <figure className="border-2 border-white/20 bg-black shadow-[8px_8px_0_0_#F59E0B]">
                <figcaption className="flex items-center justify-between gap-4 border-b-2 border-white/20 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-white/50">
                  <span>Example</span>
                  <span className="text-right">AI with a harness</span>
                </figcaption>
                <div className="space-y-5 p-5 font-mono text-sm leading-relaxed sm:p-6">
                  <p>
                    <span className="text-white/40">you &gt; </span>
                    Which deals went quiet this month? Draft a follow-up for each one.
                  </p>
                  <ul className="space-y-2">
                    {exampleRun.map((line) => (
                      <li key={line.text} className="flex gap-3">
                        <span className="w-12 shrink-0 text-vision-gold">{line.tag}</span>
                        <span className="min-w-0 break-words text-white/60">{line.text}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="flex gap-3 border-t border-white/15 pt-5 text-white">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-vision-gold" aria-hidden="true" />
                    <span>14 deals found. 14 follow-ups drafted and waiting for your approval.</span>
                  </p>
                </div>
              </figure>
            </div>
          </section>

          {/* Clients */}
          <section className="border-b border-white/10 py-12">
            <div className="container">
              <p className="mb-8 text-center text-xs font-bold uppercase tracking-[0.2em] text-white/40">
                Trusted by teams at
              </p>
              <ul className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
                {clients.map((c) => (
                  <li key={c.name}>
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xl font-black tracking-tight text-white/50 transition-colors hover:text-white"
                    >
                      {c.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Glossary */}
          <section id="harness" className="scroll-mt-20 py-24 md:py-32">
            <div className="container">
              <div className="mb-14 max-w-2xl">
                <p className="mb-4 text-sm font-bold uppercase tracking-[0.2em] text-vision-gold">In plain words</p>
                <h2 className="text-4xl font-black uppercase leading-none md:text-6xl">What a harness is</h2>
              </div>
              <dl className="grid gap-x-12 gap-y-10 md:grid-cols-2">
                {glossary.map(({ term, definition }) => (
                  <div key={term} className="border-l-4 border-vision-gold pl-6">
                    <dt className="mb-2 text-2xl font-black uppercase">{term}</dt>
                    <dd className="leading-relaxed text-white/65">{definition}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>

          {/* Services */}
          <section id="services" className="scroll-mt-20 border-y border-white/10 bg-white/[0.02] py-24 md:py-32">
            <div className="container">
              <div className="mb-14 max-w-2xl">
                <p className="mb-4 text-sm font-bold uppercase tracking-[0.2em] text-vision-gold">What we set up</p>
                <h2 className="text-4xl font-black uppercase leading-none md:text-6xl">The whole harness, one team</h2>
              </div>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {offerings.map(({ id, icon: Icon, title, text }) => (
                  <Link
                    key={id}
                    to={`/services#${id}`}
                    className="group flex flex-col border-2 border-white/15 bg-black p-8 transition-all hover:-translate-x-1 hover:-translate-y-1 hover:border-white hover:shadow-[6px_6px_0_0_#F59E0B]"
                  >
                    <Icon className="mb-6 h-8 w-8 text-vision-gold" aria-hidden="true" />
                    <h3 className="mb-3 text-2xl font-black uppercase">{title}</h3>
                    <p className="leading-relaxed text-white/65">{text}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Process */}
          <section id="process" className="scroll-mt-20 py-24 md:py-32">
            <div className="container">
              <div className="mb-14 max-w-2xl">
                <p className="mb-4 text-sm font-bold uppercase tracking-[0.2em] text-vision-gold">How we work</p>
                <h2 className="text-4xl font-black uppercase leading-none md:text-6xl">One job at a time</h2>
              </div>
              <ol className="grid gap-10 md:grid-cols-3 md:gap-8">
                {steps.map((step, i) => (
                  <li key={step.title} className="border-t-4 border-vision-gold pt-6">
                    <span className="text-sm font-black text-white/40">0{i + 1}</span>
                    <h3 className="mb-3 mt-2 text-3xl font-black uppercase">{step.title}</h3>
                    <p className="leading-relaxed text-white/65">{step.text}</p>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          {/* Open source */}
          <section className="border-y border-white/10 bg-white/[0.02] py-24 md:py-32">
            <div className="container">
              <div className="mb-14 max-w-2xl">
                <p className="mb-4 text-sm font-bold uppercase tracking-[0.2em] text-vision-gold">Built in the open</p>
                <h2 className="text-4xl font-black uppercase leading-none md:text-6xl">Harnesses you can read</h2>
                <p className="mt-6 text-lg leading-relaxed text-white/65">
                  Our founder publishes his harness work on GitHub. Read the code before you talk to us.
                </p>
              </div>
              <ul className="grid gap-6 sm:grid-cols-2">
                {openSource.map((project) => (
                  <li key={project.name}>
                    <a
                      href={project.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex h-full flex-col border-2 border-white/15 bg-black p-8 transition-colors hover:border-white"
                    >
                      <span className="mb-3 flex items-center justify-between gap-4">
                        <span className="min-w-0 break-words font-mono text-lg font-bold text-white">{project.name}</span>
                        <ArrowUpRight
                          className="h-5 w-5 shrink-0 text-white/40 transition-colors group-hover:text-vision-gold"
                          aria-hidden="true"
                        />
                      </span>
                      <span className="leading-relaxed text-white/65">{project.text}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Testimonial */}
          <section className="py-24 md:py-32">
            <div className="container max-w-4xl text-center">
              <blockquote className="text-2xl font-bold leading-snug md:text-4xl">
                “We needed a reliable partner to scale quickly. The solution shipped on time and performed even better
                than we hoped.”
              </blockquote>
              <div className="mt-8 flex items-center justify-center gap-3">
                <img src="/francis.jpg" alt="" className="h-12 w-12 border-2 border-vision-gold object-cover" width="48" height="48" loading="lazy" />
                <div className="text-left">
                  <p className="font-bold">Francis</p>
                  <p className="text-sm text-white/50">Operations Lead</p>
                </div>
              </div>
              <Link
                to="/portfolio"
                className="mt-12 inline-flex items-center gap-2 font-bold uppercase tracking-wider text-vision-gold hover:underline"
              >
                See our work <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </section>

          {/* Final CTA */}
          <section className="container pb-24 md:pb-32">
            <div className="border-2 border-black bg-vision-gold px-6 py-16 text-center text-black shadow-[10px_10px_0_0_#fff] md:py-20">
              <h2 className="mx-auto max-w-3xl text-4xl font-black uppercase leading-none md:text-6xl">
                What should your AI be doing?
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-lg font-medium text-black/75">
                Book a free call. We'll look at how your team works and name the first job worth handing over.
              </p>
              <a
                href={BOOK_CALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={bookCall("Final CTA")}
                className="mt-10 inline-flex items-center gap-2 border-2 border-black bg-black px-8 py-4 font-black uppercase tracking-wider text-white transition-transform hover:-translate-y-0.5"
              >
                Book a free call <ArrowRight className="h-5 w-5" />
              </a>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Index;
